import React from 'react';
import { motion } from 'framer-motion';
import './BlockhatAuditBanner.css';

interface BlockhatAuditBannerProps {
  className?: string;
}

const BlockhatAuditBanner: React.FC<BlockhatAuditBannerProps> = ({ className }) => {
  const containerVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  const auditHighlights = [
    {
      title: "Solana Audit",
      description: "Completed",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"/>
        </svg>
      )
    },
    {
      title: "Smart Contract",
      description: "Verified",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17A2 2 0 0 0 14 15C14 13.89 13.1 13 12 13A2 2 0 0 0 10 15A2 2 0 0 0 12 17M18 8A2 2 0 0 1 20 10V20A2 2 0 0 1 18 22H6A2 2 0 0 1 4 20V10C4 8.89 4.89 8 6 8H7V6A5 5 0 0 1 12 1A5 5 0 0 1 17 6V8H18M12 3A3 3 0 0 0 9 6V8H15V6A3 3 0 0 0 12 3Z"/>
        </svg>
      )
    },
    {
      title: "500+ Audits",
      description: "Track record",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M13 3V9.5L16.5 13V21H7.5V13L11 9.5V3H13M12 1H10V8.5L6.5 12V23H17.5V12L14 8.5V1H12Z"/>
        </svg>
      )
    },
    {
      title: "$10B+ Secured",
      description: "Assets protected",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"/>
        </svg>
      )
    }
  ];

  return (
    <section className={`blockhat-audit-banner ${className || ''}`}>
      <div className="container">
        <motion.div
          className="blockhat-banner"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="blockhat-content" variants={itemVariants}>
            {/* Left: Logo + Title */}
            <div className="blockhat-main-info">
              <div className="blockhat-logo-container">
                <img 
                  src="/BlockhatLogo.png" 
                  alt="Blockhat" 
                  className="blockhat-logo" 
                />
              </div>

              <div className="blockhat-title-section">
                <h3 className="blockhat-title">USD.P Solana: Blockhat Audited</h3>
                <p className="blockhat-subtitle">Comprehensive Solana smart contract security audit - Q2 2026</p>
              </div>
            </div>

            {/* Middle: Compact highlights */}
            <motion.div className="audit-highlights" variants={itemVariants}>
              {auditHighlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  className="highlight-item"
                  variants={itemVariants}
                  whileHover={{ y: -1 }}
                >
                  <div className="highlight-icon">
                    {highlight.icon}
                  </div>
                  <h4 className="highlight-title">{highlight.title}</h4>
                  <p className="highlight-description">{highlight.description}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Right: Score + Actions */}
            <div className="blockhat-right-section">
              <motion.div className="blockhat-actions" variants={itemVariants}>
                <a
                  href="/docs/security-audits"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-button primary"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                  </svg>
                  Blockhat Report
                </a>
                <a
                  href="https://solscan.io/token/USDPws3dWXb5Z54H7U7aPjP8wnDY37uqm3DZeZqLPBW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-button secondary"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                  </svg>
                  Solscan
                </a>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default BlockhatAuditBanner;
